/**
 * ComplaintModal — Create / Edit complaint modal
 *
 * Handles both:
 *  - Create mode: "Log Complaint" → auto-shows deadline preview based on type
 *  - Edit mode: Pre-populates all fields with existing complaint data
 *
 * Consumed by:
 *  - complaints.tsx (list page) — create mode
 *  - complaint-detail.tsx — edit mode
 */

import { useEffect, useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2, Calendar } from "lucide-react";
import { useToast } from "@/hooks/use-toast";
import { apiRequest } from "@/lib/queryClient";
import { format, addDays } from "date-fns";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

interface ComplaintData {
  id?: string;
  complainantName?: string | null;
  complainantEmail?: string | null;
  complainantPhone?: string | null;
  complainantType?: string | null;
  complaintType?: string;
  complaintCategory?: string;
  complaintSummary?: string | null;
  complaintDetail?: string | null;
  receivedDate?: string | null;
  dealId?: string | null;
}

interface ComplaintModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  editingComplaint?: ComplaintData | null;
  onSuccess?: (complaintId: string) => void;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const COMPLAINT_TYPES = [
  { value: "standard", label: "Standard" },
  { value: "hardship", label: "Hardship" },
  { value: "default_notice", label: "Default Notice" },
];

const COMPLAINT_CATEGORIES = [
  { value: "service", label: "Service" },
  { value: "product", label: "Product" },
  { value: "fees", label: "Fees" },
  { value: "communication", label: "Communication" },
  { value: "advice", label: "Advice" },
  { value: "privacy", label: "Privacy" },
  { value: "other", label: "Other" },
];

const COMPLAINANT_TYPES = [
  { value: "individual", label: "Individual" },
  { value: "business", label: "Business" },
  { value: "third_party", label: "Third Party" },
];

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

/**
 * Calculate auto-deadline preview dates based on received date and complaint type.
 * standard: +30 days, hardship/default_notice: +21 days
 */
function calculateDeadlines(
  receivedDate: string,
  complaintType: string
): { ackDue: string; resolutionDue: string } | null {
  if (!receivedDate || !complaintType) return null;
  try {
    const received = new Date(receivedDate);
    if (isNaN(received.getTime())) return null;

    const ackDue = addDays(received, 1); // 1 business day ack
    const resolutionDays = complaintType === "standard" ? 30 : 21;
    const resolutionDue = addDays(received, resolutionDays);

    return {
      ackDue: format(ackDue, "d MMM yyyy"),
      resolutionDue: format(resolutionDue, "d MMM yyyy"),
    };
  } catch {
    return null;
  }
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function ComplaintModal({
  open,
  onOpenChange,
  editingComplaint,
  onSuccess,
}: ComplaintModalProps) {
  const { toast } = useToast();
  const queryClient = useQueryClient();

  const isEditMode = !!editingComplaint?.id;

  // Form state
  const [complainantName, setComplainantName] = useState("");
  const [complainantEmail, setComplainantEmail] = useState("");
  const [complainantPhone, setComplainantPhone] = useState("");
  const [complainantType, setComplainantType] = useState("individual");
  const [complaintType, setComplaintType] = useState("");
  const [complaintCategory, setComplaintCategory] = useState("");
  const [complaintSummary, setComplaintSummary] = useState("");
  const [complaintDetail, setComplaintDetail] = useState("");
  const [receivedDate, setReceivedDate] = useState(
    new Date().toISOString().split("T")[0]
  );

  // Reset / pre-populate form when modal opens or editingComplaint changes
  useEffect(() => {
    if (open && editingComplaint) {
      setComplainantName(editingComplaint.complainantName ?? "");
      setComplainantEmail(editingComplaint.complainantEmail ?? "");
      setComplainantPhone(editingComplaint.complainantPhone ?? "");
      setComplainantType(editingComplaint.complainantType ?? "individual");
      setComplaintType(editingComplaint.complaintType ?? "");
      setComplaintCategory(editingComplaint.complaintCategory ?? "");
      setComplaintSummary(editingComplaint.complaintSummary ?? "");
      setComplaintDetail(editingComplaint.complaintDetail ?? "");
      setReceivedDate(
        editingComplaint.receivedDate ?? new Date().toISOString().split("T")[0]
      );
    } else if (open && !editingComplaint) {
      // Reset for create mode
      setComplainantName("");
      setComplainantEmail("");
      setComplainantPhone("");
      setComplainantType("individual");
      setComplaintType("");
      setComplaintCategory("");
      setComplaintSummary("");
      setComplaintDetail("");
      setReceivedDate(new Date().toISOString().split("T")[0]);
    }
  }, [open, editingComplaint]);

  // Deadline preview computed from receivedDate + complaintType
  const deadlinePreview = calculateDeadlines(receivedDate, complaintType);

  // Form validity
  const isValid =
    complainantName.trim().length > 0 &&
    complaintType.length > 0 &&
    complaintCategory.length > 0 &&
    complaintSummary.trim().length > 0 &&
    receivedDate.length > 0;

  const handleOpenChange = (newOpen: boolean) => {
    if (!newOpen) {
      // Will be reset by useEffect on next open
    }
    onOpenChange(newOpen);
  };

  // ---------------------------------------------------------------------------
  // Mutations
  // ---------------------------------------------------------------------------

  const createMutation = useMutation({
    mutationFn: async () => {
      const response = await apiRequest("POST", "/api/compliance/complaints", {
        complainantName: complainantName.trim(),
        complainantEmail: complainantEmail.trim() || undefined,
        complainantPhone: complainantPhone.trim() || undefined,
        complainantType,
        complaintType,
        complaintCategory,
        complaintSummary: complaintSummary.trim(),
        complaintDetail: complaintDetail.trim() || undefined,
        receivedDate,
      });
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to create complaint");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/complaints"] });
      toast({ title: "Complaint logged successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const editMutation = useMutation({
    mutationFn: async () => {
      if (!editingComplaint?.id) throw new Error("No complaint ID");
      const response = await apiRequest(
        "PATCH",
        `/api/compliance/complaints/${editingComplaint.id}`,
        {
          complainantName: complainantName.trim(),
          complainantEmail: complainantEmail.trim() || undefined,
          complainantPhone: complainantPhone.trim() || undefined,
          complainantType,
          complaintType,
          complaintCategory,
          complaintSummary: complaintSummary.trim() || undefined,
          complaintDetail: complaintDetail.trim() || undefined,
          receivedDate,
        }
      );
      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.error ?? "Failed to update complaint");
      }
      return response.json();
    },
    onSuccess: (data) => {
      queryClient.invalidateQueries({ queryKey: ["/api/compliance/complaints"] });
      queryClient.invalidateQueries({
        queryKey: [`/api/compliance/complaints/${data.id}`],
      });
      toast({ title: "Complaint updated successfully" });
      handleOpenChange(false);
      onSuccess?.(data.id);
    },
    onError: (err: Error) => {
      toast({
        title: "Error",
        description: err.message,
        variant: "destructive",
      });
    },
  });

  const isPending = createMutation.isPending || editMutation.isPending;

  const handleSubmit = () => {
    if (!isValid) return;
    if (isEditMode) {
      editMutation.mutate();
    } else {
      createMutation.mutate();
    }
  };

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-2xl max-h-[90vh] overflow-y-auto"
        data-testid="modal-complaint-form"
      >
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? "Edit Complaint" : "Log Complaint"}
          </DialogTitle>
          <DialogDescription>
            {isEditMode
              ? "Update the complaint details below."
              : "Log a new IDR complaint per ASIC RG 271."}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {/* Complainant details */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Complainant
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Complainant Name */}
              <div className="col-span-2 space-y-1.5">
                <Label htmlFor="complainant-name">
                  Complainant Name <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="complainant-name"
                  data-testid="input-complainant-name"
                  value={complainantName}
                  onChange={(e) => setComplainantName(e.target.value)}
                  placeholder="Full name of complainant"
                />
              </div>

              {/* Email */}
              <div className="space-y-1.5">
                <Label htmlFor="complainant-email">Email</Label>
                <Input
                  id="complainant-email"
                  data-testid="input-complainant-email"
                  type="email"
                  value={complainantEmail}
                  onChange={(e) => setComplainantEmail(e.target.value)}
                  placeholder="email@example.com"
                />
              </div>

              {/* Phone */}
              <div className="space-y-1.5">
                <Label htmlFor="complainant-phone">Phone</Label>
                <Input
                  id="complainant-phone"
                  data-testid="input-complainant-phone"
                  value={complainantPhone}
                  onChange={(e) => setComplainantPhone(e.target.value)}
                  placeholder="04XX XXX XXX"
                />
              </div>

              {/* Complainant Type */}
              <div className="space-y-1.5">
                <Label>Complainant Type</Label>
                <Select
                  value={complainantType}
                  onValueChange={setComplainantType}
                >
                  <SelectTrigger data-testid="select-complainant-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLAINANT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
          </div>

          {/* Complaint classification */}
          <div className="space-y-2">
            <h3 className="text-sm font-semibold text-muted-foreground uppercase tracking-wide">
              Classification
            </h3>

            <div className="grid grid-cols-2 gap-4">
              {/* Complaint Type */}
              <div className="space-y-1.5">
                <Label>
                  Complaint Type <span className="text-destructive">*</span>
                </Label>
                <Select value={complaintType} onValueChange={setComplaintType}>
                  <SelectTrigger data-testid="select-complaint-type">
                    <SelectValue placeholder="Select type" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLAINT_TYPES.map((t) => (
                      <SelectItem key={t.value} value={t.value}>
                        {t.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Complaint Category */}
              <div className="space-y-1.5">
                <Label>
                  Category <span className="text-destructive">*</span>
                </Label>
                <Select
                  value={complaintCategory}
                  onValueChange={setComplaintCategory}
                >
                  <SelectTrigger data-testid="select-complaint-category">
                    <SelectValue placeholder="Select category" />
                  </SelectTrigger>
                  <SelectContent>
                    {COMPLAINT_CATEGORIES.map((c) => (
                      <SelectItem key={c.value} value={c.value}>
                        {c.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Received Date */}
              <div className="space-y-1.5">
                <Label htmlFor="received-date">
                  Date Received <span className="text-destructive">*</span>
                </Label>
                <Input
                  id="received-date"
                  data-testid="input-received-date"
                  type="date"
                  value={receivedDate}
                  onChange={(e) => setReceivedDate(e.target.value)}
                />
              </div>
            </div>
          </div>

          {/* Summary and detail */}
          <div className="space-y-4">
            <div className="space-y-1.5">
              <Label htmlFor="complaint-summary">
                Summary <span className="text-destructive">*</span>
              </Label>
              <Input
                id="complaint-summary"
                data-testid="input-complaint-summary"
                value={complaintSummary}
                onChange={(e) => setComplaintSummary(e.target.value)}
                placeholder="Brief description of the complaint"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="complaint-detail">Detail</Label>
              <Textarea
                id="complaint-detail"
                data-testid="input-complaint-detail"
                value={complaintDetail}
                onChange={(e) => setComplaintDetail(e.target.value)}
                placeholder="Detailed account of the complaint..."
                rows={3}
              />
            </div>
          </div>

          {/* Auto-deadline preview (create mode only, or when receivedDate + type filled) */}
          {deadlinePreview && !isEditMode && (
            <div
              className="rounded-md border bg-muted/50 p-3 space-y-2"
              data-testid="deadline-preview"
            >
              <div className="flex items-center gap-1.5 text-sm font-medium">
                <Calendar className="h-4 w-4 text-muted-foreground" />
                Auto-calculated Deadlines
              </div>
              <div className="grid grid-cols-2 gap-3 text-sm">
                <div>
                  <span className="text-muted-foreground">
                    Acknowledgement due:{" "}
                  </span>
                  <span className="font-medium">{deadlinePreview.ackDue}</span>
                </div>
                <div>
                  <span className="text-muted-foreground">Resolution due: </span>
                  <span className="font-medium">
                    {deadlinePreview.resolutionDue}
                  </span>
                </div>
              </div>
            </div>
          )}
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={() => handleOpenChange(false)}
            disabled={isPending}
          >
            Cancel
          </Button>
          <Button
            data-testid="button-save"
            onClick={handleSubmit}
            disabled={!isValid || isPending}
          >
            {isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {isPending
              ? "Saving..."
              : isEditMode
              ? "Save Changes"
              : "Log Complaint"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
